import { Tooltip, Typography, Grid, useMediaQuery, useTheme, Box } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from '@mui/material/Skeleton';
import { CustomTooltipContent } from '../../ClientTable/Columns';
import { transformOverallEngagementData } from '../../../utils/dateUtils';
import { EngagementLevel } from '../../../redux/client/clientSlice';
import OverallEngagementMeter from '../../EngagementMeter/OverallEngagementMeter';
import { useAppSelector } from '../../../redux/hooks';
import { CircularSkeleton, RoundedSkeleton } from '../../ClientTable/LoadingSkeleton';

interface OverallEngagementProps {
  transformedEngageData: EngagementLevel[];
}

function OverallEngagement({ transformedEngageData }: OverallEngagementProps) {
  const { t } = useTranslation();
  const [startIndex, setStartIndex] = useState<number>(0);
  const [overallEngagementData, setOverallEngagementData] = useState<
    { date: string; engagementLevel: string }[]
  >([]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [maximumItemCount, setMaximumItemCount] = useState(20);
  const clientActivityProgressLoading = useAppSelector(
    (state) => state.client.clientActivityProgressLoading
  );

  useEffect(() => {
    if (isSmallScreen) {
      setMaximumItemCount(11);
    } else {
      setMaximumItemCount(20);
    }
  }, [isSmallScreen]);

  useEffect(() => {
    setOverallEngagementData(transformOverallEngagementData(transformedEngageData));
  }, [transformedEngageData]);

  const handleNext = () => {
    if (startIndex + maximumItemCount < overallEngagementData.length) {
      setStartIndex(startIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (startIndex > 0) {
      setStartIndex(startIndex - 1);
    }
  };

  return (
    <>
      <Grid gap="12px" display="flex" flexDirection="row">
        {clientActivityProgressLoading ? (
          <RoundedSkeleton variant="rounded" height={17.5} width={195} />
        ) : (
          <Typography variant="h6" color="primary">
            {t('clientProgress.overallEngagement')}
          </Typography>
        )}

        {clientActivityProgressLoading ? (
          <CircularSkeleton variant="circular" width={20} height={20} />
        ) : (
          <Tooltip
            title={<CustomTooltipContent />}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#FFF',
                  borderRadius: '5px',
                  border: '1px solid #FEE4DB',
                  boxShadow: '0px 4px 40px 0px rgba(255, 181, 171, 0.20)',
                },
              },
              arrow: {
                sx: {
                  '&:before': {
                    bgcolor: '#FFF',
                    border: '1px solid #FEE4DB',
                    boxShadow: '0px 4px 40px 0px rgba(255, 181, 171, 0.20)',
                  },
                },
              },
            }}
          >
            <img
              src="/assets/icons/info-circle.svg"
              alt="i-icon"
              style={{ height: 20, width: 20 }}
            />
          </Tooltip>
        )}
      </Grid>
      {!clientActivityProgressLoading && transformedEngageData.length === 0 ? (
        <Box width="100%" display="flex" alignItems="center" justifyContent="center">
          <Typography variant="bodyBold" color="rgba(42, 60, 103, 0.20)">
            {t('clientProgress.noData')}
          </Typography>
        </Box>
      ) : (
        <Grid display="flex" alignItems="center" margin="auto" flexDirection="row" gap="12px">
          {clientActivityProgressLoading ? (
            <Skeleton variant="rounded" width={14} height={28} animation="wave" />
          ) : (
            startIndex !== 0 && (
              <Grid onClick={handlePrevious}>
                <img
                  src="/assets/icons/alt-arrow-left-large.svg"
                  alt="alt-arrow-left-large"
                  style={{ cursor: 'pointer' }}
                />
              </Grid>
            )
          )}
          <OverallEngagementMeter
            engageData={overallEngagementData.slice(startIndex, maximumItemCount + startIndex)}
            size={42}
            clientActivityProgressLoading={clientActivityProgressLoading}
          />
          {clientActivityProgressLoading ? (
            <Skeleton variant="rounded" width={14} height={28} animation="wave" />
          ) : (
            maximumItemCount + startIndex !== overallEngagementData.length &&
            overallEngagementData.length > maximumItemCount && (
              <Grid onClick={handleNext}>
                <img
                  src="/assets/icons/alt-arrow-right-large.svg"
                  alt="alt-arrow-right-large"
                  style={{ cursor: 'pointer' }}
                />
              </Grid>
            )
          )}
        </Grid>
      )}
    </>
  );
}

export default OverallEngagement;
